import { Bar, BarChart, Cell, XAxis, YAxis } from "recharts";
import { usd } from "../../lib/format";
import type { AgentCostRow } from "../../types";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { type ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "../ui/chart";
import { formatMinutes } from "./metrics";

const ROW_HEIGHT = 28;
const MIN_HEIGHT = 140;

const METRICS: Array<{
  key: "totalCostUsd" | "avgRunMinutes" | "sessions";
  title: string;
  format: (value: number) => string;
  showLabels: boolean;
}> = [
  { key: "totalCostUsd", title: "Total cost", format: usd, showLabels: true },
  { key: "avgRunMinutes", title: "Avg run time", format: formatMinutes, showLabels: false },
  {
    key: "sessions",
    title: "Sessions created",
    format: (value) => value.toLocaleString(),
    showLabels: false,
  },
];

/** Lifetime cost, run time and session volume per factory agent type, as three aligned bar charts. */
export function AgentPerformanceChart({ rows }: { rows: AgentCostRow[] }) {
  if (!rows.length) return null;
  const sorted = [...rows].sort((a, b) => b.totalCostUsd - a.totalCostUsd);
  const data = sorted.map((row) => ({ ...row, avgRunMinutes: row.avgRunMinutes ?? 0 }));
  const config = Object.fromEntries(
    sorted.map((row) => [row.agentId, { label: row.name, color: row.color }]),
  ) satisfies ChartConfig;
  const height = Math.max(MIN_HEIGHT, sorted.length * ROW_HEIGHT);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Agent performance</CardTitle>
        <CardDescription>Lifetime cost, run time and session volume by agent type</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-6 lg:grid-cols-3">
        {METRICS.map((metric) => (
          <div key={metric.key}>
            <p className="mb-2 text-xs font-medium text-muted-foreground">{metric.title}</p>
            <ChartContainer config={config} className="aspect-auto w-full" style={{ height }}>
              <BarChart data={data} layout="vertical" margin={{ left: 4, right: 12, top: 0, bottom: 0 }}>
                <XAxis type="number" hide />
                <YAxis
                  dataKey="agentId"
                  type="category"
                  tickLine={false}
                  axisLine={false}
                  width={metric.showLabels ? 120 : 1}
                  tick={metric.showLabels ? undefined : false}
                  tickFormatter={(value) => (config[value as string]?.label as string) ?? value}
                  interval={0}
                />
                <ChartTooltip
                  cursor={{ fill: "var(--muted)" }}
                  content={
                    <ChartTooltipContent
                      hideIndicator
                      formatter={(value, _name, item) => (
                        <div className="flex w-full items-center justify-between gap-4">
                          <span className="text-muted-foreground">
                            {config[item.payload.agentId as string]?.label as string}
                          </span>
                          <span className="font-mono font-medium tabular-nums">
                            {metric.format(Number(value))}
                          </span>
                        </div>
                      )}
                    />
                  }
                />
                <Bar dataKey={metric.key} radius={[0, 3, 3, 0]}>
                  {data.map((row) => (
                    <Cell key={row.agentId} fill={row.color} />
                  ))}
                </Bar>
              </BarChart>
            </ChartContainer>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
